/**
 * useMiddlewareClient hook
 *
 * Provides a properly configured MiddlewareClient with user authentication
 * for use in React components.
 */

import { useMemo } from 'react';
import { MiddlewareClient, UserAuthProvider } from '@qwickforge/middleware-client/browser';
import { useAuth } from '@qwickapps/auth-client';

export interface UseMiddlewareClientOptions {
  baseUrl?: string;
}

/**
 * React hook that creates and manages a MiddlewareClient instance
 *
 * Automatically uses the current user's session from useAuth()
 *
 * @param options - Optional configuration (baseUrl)
 * @returns MiddlewareClient instance or null if not authenticated
 *
 * @example
 * ```tsx
 * import { useMiddlewareClient } from '@qwickforge/react-client';
 *
 * function MyComponent() {
 *   const client = useMiddlewareClient();
 *
 *   useEffect(() => {
 *     if (client) {
 *       client.projects.list().then(setProjects);
 *     }
 *   }, [client]);
 * }
 * ```
 */
export function useMiddlewareClient(options?: UseMiddlewareClientOptions) {
  const { session } = useAuth();
  const baseUrl = options?.baseUrl;

  const client = useMemo(() => {
    if (!session?.accessToken || !session?.user?.id) {
      return null;
    }

    const auth = new UserAuthProvider(
      async () => session.accessToken!,
      session.user.id
    );

    return new MiddlewareClient(
      {
        baseUrl: baseUrl || (import.meta as any).env?.VITE_API_URL || 'http://localhost:8883',
      },
      auth
    );
  }, [session?.accessToken, session?.user?.id, baseUrl]);

  return client;
}
